import React, { useEffect, useState } from "react";
import { Form, Input, Button, Spin, Row, Col, DatePicker, TimePicker, Select, InputNumber, Modal } from "antd";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import { isEmpty, omit, union, uniqBy } from "lodash";
import { useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import "./Order.scss";
import AddressInput from "../../Inputs/AddressInput";
import InfoLabel from "../../Elements/InfoLabel";
import LabelComponent from "../../Elements/LabelComponent";
import { TIME_FORMAT_FRONTEND, DATE_FORMAT_FRONTEND, DATE_FORMAT_QUERY } from "../../../constants";
import { getTranslation, setCountryCookie } from "../../../utils/helper";
import DefaultContactLocation from "./DefaultContactLocation";
import TagInput from "../../Elements/TagInput";
import dayjs from "dayjs";

const OrderForm = ({ isFetching, doCreateOrder, groups, query, countryCodeInit }) => {
  const [form] = Form.useForm();
  const [types, setTypes] = useState([]);
  const [labelIds, setLabelIds] = useState([]);
  const user = useSelector(state => state.auth.user);
  const setting = useSelector(state => state.setting.setting) || user.company;
  const routePlanType = query?.routePlanType || setting?.routePlanType || "PDP";
  const routePlanId = query?.routePlanId;
  const deliveryDate = query?.deliveryDate;

  const [countryPickupPhone, setCountryPickupPhone] = useState();
  const [countryDeliveryPhone, setCountryDeliveryPhone] = useState();
  const [state, setState] = useState({
    visibleModal: false,
    contactType: null,
    contact: null,
  });

  useEffect(() => {
    if (deliveryDate && dayjs(deliveryDate).isValid()) {
      form.setFieldsValue({
        deliveryDate: dayjs(deliveryDate),
      });
    }
  }, [deliveryDate]);

  const handleOnFinish = async values => {
    const params = setParams(values);
    await doCreateOrder(params);
  };

  const setParams = values => {
    let params = { ...values };
    params.pickupPhone =
      !isEmpty(values.pickupPhone) && countryPickupPhone?.dialCode !== values.pickupPhone
        ? `${values.pickupPhone.includes("+") ? "" : "+"}${values.pickupPhone}`
        : null;
    params.deliveryPhone =
      !isEmpty(values.deliveryPhone) && countryDeliveryPhone?.dialCode !== values.deliveryPhone
        ? `${values.deliveryPhone.includes("+") ? "" : "+"}${values.deliveryPhone}`
        : null;
    params.pickupAddress = values.pickupAddress?.address;
    params.pickupLat = values.pickupAddress?.latLng?.lat;
    params.pickupLng = values.pickupAddress?.latLng?.lng;
    params.timeStart = values.timeStart ? values.timeStart.format(TIME_FORMAT_FRONTEND) : null;
    params.timeEnd = values.timeEnd ? values.timeEnd.format(TIME_FORMAT_FRONTEND) : null;
    params.pickupDuration = values.pickupDuration ? Number(values.pickupDuration) : null;
    params.deliveryAddress = values.deliveryAddress?.address;
    params.deliveryLat = values.deliveryAddress?.latLng?.lat;
    params.deliveryLng = values.deliveryAddress?.latLng?.lng;
    params.deliveryTimeStart = values.deliveryTimeStart ? values.deliveryTimeStart.format(TIME_FORMAT_FRONTEND) : null;
    params.deliveryTimeEnd = values.deliveryTimeEnd ? values.deliveryTimeEnd.format(TIME_FORMAT_FRONTEND) : null;
    params.deliveryDuration = values.deliveryDuration ? Number(values.deliveryDuration) : null;
    params.deliveryDate = values.deliveryDate ? values.deliveryDate.format(DATE_FORMAT_QUERY) : null;
    params.requiredSkills = types;
    params.labels = labelIds;
    params.fields = {};
    values.fields?.forEach(field => {
      params.fields[field.name] = field.value;
    });

    if (!isEmpty(routePlanId)) {
      params.routePlan = routePlanId;
    }

    if (!params["pickupAddress"] && !params["pickupLat"]) {
      params = omit(params, [
        "pickupName",
        "pickupAddress",
        "pickupLat",
        "pickupLng",
        "pickupPhone",
        "pickupEmail",
        "pickupDuration",
        "timeStart",
        "timeEnd",
        "pickupNotes",
      ]);
    }

    return params;
  };

  const handleOnChangePickupFrom = value => {
    form.setFieldsValue({ timeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("timeEnd"))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue("timeEnd") < value) {
      form.setFields([
        {
          name: "timeStart",
          errors: ["Shift End must be less than Shift Start"],
        },
      ]);
    }
  };

  const handleOnChangePickupTo = value => {
    form.setFieldsValue({ timeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("timeStart"))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue("timeStart") > value) {
      form.setFields([
        {
          name: "timeEnd",
          errors: ["Pickup To must be greater than Pickup From"],
        },
      ]);
    }
  };

  const resetErrorPickupTime = () => {
    form.setFields([
      {
        name: "timeStart",
        errors: [],
      },
      {
        name: "timeEnd",
        errors: [],
      },
    ]);
  };

  const handleOnChangeDeliveryFrom = value => {
    form.setFieldsValue({ deliveryTimeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("deliveryTimeEnd"))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue("deliveryTimeEnd") < value) {
      form.setFields([
        {
          name: "deliveryTimeStart",
          errors: ["Delivery From must be less than Delivery To"],
        },
      ]);
    }
  };

  const handleOnChangeDeliveryTo = value => {
    form.setFieldsValue({ deliveryTimeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("deliveryTimeStart"))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue("deliveryTimeStart") > value) {
      form.setFields([
        {
          name: "deliveryTimeEnd",
          errors: ["Delivery To must be greater than Delivery From"],
        },
      ]);
    }
  };

  const resetErrorDeliveryTime = () => {
    form.setFields([
      {
        name: "deliveryTimeStart",
        errors: [],
      },
      {
        name: "deliveryTimeEnd",
        errors: [],
      },
    ]);
  };

  const handleUseDefaultLocation = (e, type) => {
    e.preventDefault();
    setState({
      ...state,
      visibleModal: true,
      contactType: type,
      contact: null,
    });
  };

  const handleSubmitSelectContact = () => {
    if (state.contact) {
      if (state.contactType === "PICKUP") {
        form.setFieldsValue({
          pickupName: state.contact.name,
          pickupPhone: state.contact.phone,
          timeStart: state.contact.startTime ? dayjs(state.contact.startTime, TIME_FORMAT_FRONTEND) : null,
          timeEnd: state.contact.endTime ? dayjs(state.contact.endTime, TIME_FORMAT_FRONTEND) : null,
          pickupDuration: state.contact.duration,
          pickupAddress: {
            address: state.contact.address,
            latLng: {
              lat: state.contact.lat,
              lng: state.contact.lng,
            },
          },
          pickupNotes: state.contact.notes,
        });
      }
      if (state.contactType === "DELIVERY") {
        form.setFieldsValue({
          deliveryName: state.contact.name,
          deliveryPhone: state.contact.phone,
          deliveryTimeStart: state.contact.startTime ? dayjs(state.contact.startTime, TIME_FORMAT_FRONTEND) : null,
          deliveryTimeEnd: state.contact.endTime ? dayjs(state.contact.endTime, TIME_FORMAT_FRONTEND) : null,
          deliveryDuration: state.contact.duration,
          deliveryAddress: {
            address: state.contact.address,
            latLng: {
              lat: state.contact.lat,
              lng: state.contact.lng,
            },
          },
          deliveryNotes: state.contact.notes,
          load: state.contact.load,
        });
      }
      setTypes(union(types, state.contact.types));
      if (!isEmpty(state.contact.fields)) {
        const fieldsT = Object.keys(state.contact.fields).map(key => ({
          name: key,
          value: state.contact.fields[key],
        }));
        const fields = uniqBy(union(form.getFieldValue("fields") || [], fieldsT), "name");
        form.setFieldsValue({ fields });
      }
    }
    handleCancelSelectContact();
  };

  const handleCancelSelectContact = () => {
    setState({ ...state, visibleModal: false, contact: null });
  };

  return (
    <div className="OrderForm">
      <Spin spinning={isFetching}>
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleOnFinish}
          initialValues={{
            pickupAddress: {},
            deliveryAddress: {},
          }}>
          {routePlanType === "PDP" && (
            <div className="FormContent">
              <div className="Section">
                <div className="SectionHeader">
                  <div className="Left">{getTranslation(setting, "Orders", "Pickup")}</div>
                  <div className="Right">
                    <Link to="#" onClick={e => handleUseDefaultLocation(e, "PICKUP")}>
                      {getTranslation(setting, "Orders", "Add from Contact List")}
                      <ReactSVG className="icon" src="/icons/default-location.svg" />
                    </Link>
                  </div>
                </div>
                <div className="SectionContent">
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup Name")}
                            title='Name of the customer you are picking up this order from. If you are picking up this order from a warehouse, use "Warehouse" as the pickup name.'
                          />
                        }
                        name="pickupName">
                        <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Name")} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup Phone Number")}
                            title="Phone number of the customer you are picking up this order from. Remember to include the country code and plus sign e.g. +17789123456 for US/Canada numbers or +6593456789 for Singapore numbers."
                          />
                        }
                        name="pickupPhone">
                        <PhoneInput
                          country={countryCodeInit}
                          enableSearch
                          disableSearchIcon
                          onChange={(value, country) => {
                            setCountryPickupPhone(country);
                            setCountryCookie(value, country, user);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup From")}
                            title="The earliest time you need to pick up this order."
                          />
                        }
                        name="timeStart">
                        <TimePicker
                          size="large"
                          placeholder={getTranslation(setting, "Orders", "Pickup From")}
                          format={TIME_FORMAT_FRONTEND}
                          minuteStep={30}
                          style={{ width: "100%" }}
                          onChange={handleOnChangePickupFrom}
                          onCalendarChange={handleOnChangePickupFrom}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup To")}
                            title="The latest time you need to pick up this order."
                          />
                        }
                        name="timeEnd">
                        <TimePicker
                          size="large"
                          placeholder={getTranslation(setting, "Orders", "Pickup To")}
                          format={TIME_FORMAT_FRONTEND}
                          minuteStep={30}
                          style={{ width: "100%" }}
                          onChange={handleOnChangePickupTo}
                          onCalendarChange={handleOnChangePickupTo}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup Email")}
                            title="Email of the customer you are picking up this order from."
                          />
                        }
                        name="pickupEmail"
                        rules={[{ type: "email", message: "Invalid Email" }]}>
                        <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Email")} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup Address")}
                            title="Address of the customer you are picking up this order from."
                            required={routePlanType === "PDP"}
                          />
                        }
                        name="pickupAddress"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                                return Promise.resolve();
                              }

                              if (isEmpty(value?.address)) {
                                return Promise.reject(new Error("Required"));
                              }

                              return Promise.reject(new Error("Invalid"));
                            },
                          },
                        ]}>
                        <AddressInput
                          size="large"
                          placeholder={getTranslation(setting, "Orders", "Pickup Address")}
                          onSelect={value => form.setFieldsValue({ pickupAddress: value })}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup Duration")}
                            title="The time spent (in minutes) at the pickup location."
                          />
                        }
                        name="pickupDuration">
                        <Input
                          size="large"
                          placeholder={getTranslation(setting, "Orders", "Pickup Duration")}
                          suffix="minutes"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          )}
          <div className="FormContent">
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">{getTranslation(setting, "Orders", "Delivery")}</div>
                <div className="Right">
                  <Link to="#" onClick={e => handleUseDefaultLocation(e, "DELIVERY")}>
                    {getTranslation(setting, "Orders", "Add from Contact List")}
                    <ReactSVG className="icon" src="/icons/default-location.svg" />
                  </Link>
                </div>
              </div>
              <div className="SectionContent">
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery Name")}
                          title="Name of the customer you are delivering this order to."
                        />
                      }
                      name="deliveryName">
                      <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Name")} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery Phone Number")}
                          title="Phone number of the customer you are delivering this order to. If you've set up customer text notifications, the customer will receive text messages updating him on the status of this order. Remember to include the country code and plus sign e.g. +17789123456 for US/Canada numbers or +6593456789 for Singapore numbers."
                        />
                      }
                      name="deliveryPhone">
                      <PhoneInput
                        country={countryCodeInit}
                        enableSearch
                        disableSearchIcon
                        onChange={(value, country) => {
                          setCountryDeliveryPhone(country);
                          setCountryCookie(value, country, user);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery From")}
                          title="The earliest time you need to deliver this order."
                        />
                      }
                      name="deliveryTimeStart">
                      <TimePicker
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Delivery From")}
                        format={TIME_FORMAT_FRONTEND}
                        minuteStep={30}
                        style={{ width: "100%" }}
                        onChange={handleOnChangeDeliveryFrom}
                        onCalendarChange={handleOnChangeDeliveryFrom}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery To")}
                          title="The latest time you need to deliver this order."
                        />
                      }
                      name="deliveryTimeEnd">
                      <TimePicker
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Delivery To")}
                        format={TIME_FORMAT_FRONTEND}
                        minuteStep={30}
                        style={{ width: "100%" }}
                        onChange={handleOnChangeDeliveryTo}
                        onCalendarChange={handleOnChangeDeliveryTo}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery Email")}
                          title="Email of the customer you are picking up this order from. If you've set up customer email notifications, the customer will receive emails updating him on the status of this order."
                        />
                      }
                      name="deliveryEmail"
                      rules={[{ type: "email", message: "Invalid Email" }]}>
                      <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Email")} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery Address")}
                          title="Address of the customer you are delivering this order to."
                          required={true}
                        />
                      }
                      name="deliveryAddress"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (
                              (value?.address && value?.latLng?.lat && value?.latLng?.lng) ||
                              (isEmpty(value?.address) && routePlanType !== "PDP")
                            ) {
                              return Promise.resolve();
                            }

                            if (isEmpty(value?.address) && routePlanType === "PDP") {
                              return Promise.reject(new Error("Required"));
                            }

                            return Promise.reject(new Error("Invalid"));
                          },
                        },
                      ]}>
                      <AddressInput
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Delivery Address")}
                        onSelect={value => form.setFieldsValue({ deliveryAddress: value })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery Duration")}
                          title="The time spent (in minutes) at the delivery location."
                        />
                      }
                      name="deliveryDuration">
                      <Input
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Delivery Duration")}
                        suffix="minutes"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div className="FormContent">
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">{getTranslation(setting, "Orders", "Order Info")}</div>
              </div>
              <div className="SectionContent">
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Order Ref")}
                          title="Unique order reference number e.g. INV-ABC123."
                        />
                      }
                      name="orderRef">
                      <Input size="large" placeholder={getTranslation(setting, "Orders", "Order Ref")} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Date")}
                          title="Date that this order will be picked up and delivered."
                          required={true}
                        />
                      }
                      name="deliveryDate"
                      rules={[{ required: true, message: "Required" }]}>
                      <DatePicker
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Date")}
                        format={DATE_FORMAT_FRONTEND}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "User Group")}
                          title="Groups of users that can log in and track the status of this order."
                        />
                      }
                      name="group">
                      <Select size="large" showSearch optionFilterProp="children">
                        {groups.map(c => (
                          <Select.Option key={c._id} value={c._id}>
                            {c.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Load")}
                          title="How big (e.g. how many individual packages) your order is. Used together with driver capacities to determine how many stops can be done in a single route."
                        />
                      }
                      name="load">
                      <InputNumber
                        size="large"
                        placeholder={getTranslation(setting, "Orders", "Load")}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label={<InfoLabel text={getTranslation(setting, "Orders", "Labels")} title="Labels" />}>
                  <LabelComponent labelIds={labelIds} setLabelIds={setLabelIds} />
                </Form.Item>
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Types")}
                          title="For advanced users only. Use this to match specific orders with drivers that are equipped to do those orders e.g. a VIP driver can pickup and deliver VIP orders."
                        />
                      }>
                      <TagInput tags={types} onChange={values => setTypes(values)} />
                    </Form.Item>
                  </Col>
                  {routePlanType === "PDP" && (
                    <Col span={9}>
                      <Form.Item
                        label={
                          <InfoLabel
                            text={getTranslation(setting, "Orders", "Pickup Notes")}
                            title='Any information that can help the driver successfully make his delivery e.g. "Leave the package on the porch".'
                          />
                        }
                        name="pickupNotes">
                        <Input.TextArea rows={4} placeholder={getTranslation(setting, "Orders", "Pickup Notes")} />
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={9}>
                    <Form.Item
                      label={
                        <InfoLabel
                          text={getTranslation(setting, "Orders", "Delivery Notes")}
                          title='Any information that can help the driver successfully make his delivery e.g. "Leave the package on the porch".'
                        />
                      }
                      name="deliveryNotes">
                      <Input.TextArea rows={4} placeholder={getTranslation(setting, "Orders", "Delivery Notes")} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">{getTranslation(setting, "Orders", "Custom Fields")}</div>
              </div>
              <div className="SectionContent">
                <Form.List name="fields">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(field => (
                        <div key={field.key} className="Field">
                          <div className="FieldName">
                            <Form.Item
                              {...field}
                              name={[field.name, "name"]}
                              rules={[{ required: true, message: "Required" }]}>
                              <Input size="large" placeholder={getTranslation(setting, "Orders", "Field Title")} />
                            </Form.Item>
                          </div>
                          <div className="FieldValue">
                            <Form.Item
                              {...field}
                              name={[field.name, "value"]}
                              rules={[{ required: true, message: "Required" }]}>
                              <Input size="large" placeholder={getTranslation(setting, "Orders", "Field Input")} />
                            </Form.Item>
                          </div>
                          <div className="FieldAction">
                            <Button key="delete" onClick={() => remove(field.name)} size="large">
                              <ReactSVG className="icon" src="/icons/delete.svg" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <Form.Item>
                        <Button type="primary" onClick={() => add()} className="AddCustomFieldBtn">
                          {getTranslation(setting, "Orders", "Add Custom Field")}
                          <ReactSVG className="icon" src="/icons/add.svg" />
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </div>
            </div>
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Link to={query.redirectUrl ? query.redirectUrl : "/orders"}>
                <Button key="cancel">{getTranslation(setting, "Common", "Cancel")}</Button>
              </Link>
              <Button htmlType="submit" type="primary">
                {getTranslation(setting, "Common", "Create")}
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </Spin>
      <Modal open={state.visibleModal} onCancel={handleCancelSelectContact} footer={[]} width={792}>
        <DefaultContactLocation
          state={state}
          setState={setState}
          handleCancel={handleCancelSelectContact}
          handleSubmit={handleSubmitSelectContact}
        />
      </Modal>
    </div>
  );
};

export default OrderForm;
